import React, { useCallback, useMemo, useRef } from 'react';
import { types as api } from '@mesa-labs/mesa-api';
import AsyncSelect from 'react-select/async';

import { useLazyGetAllVendorsQuery, useGetVendorQuery } from '../../redux/api/vendors';

const NULL_VENDOR_VALUE = '__null__';

type VendorOption = {
  readonly label: string;
  readonly value: string;
  readonly vendor: api.VendorResponse | null;
};

type QuickOption = { readonly value: string; readonly label: string };

type VendorSelectProps = {
  readonly selectedVendorId?: string | null;
  readonly onSelectedVendorChange: (vendorId: string | null | undefined) => void;
  readonly placeholder?: string;
  readonly nullLabel?: string;
  readonly isDisabled?: boolean;
  readonly quickOptions?: readonly QuickOption[];
};

function VendorSelect({
  selectedVendorId,
  onSelectedVendorChange,
  placeholder = 'Search by vendor name or ID...',
  nullLabel,
  isDisabled = false,
  quickOptions,
}: VendorSelectProps): React.ReactElement {
  const [triggerSearch] = useLazyGetAllVendorsQuery();
  const searchIdRef = useRef(0);
  const labelCacheRef = useRef<{ vendorId: string; label: string } | null>(null);

  const hasCachedLabel = labelCacheRef.current?.vendorId === selectedVendorId;

  const { data: selectedVendor } = useGetVendorQuery(
    selectedVendorId ? { id: selectedVendorId } : { id: '' },
    { skip: !selectedVendorId || hasCachedLabel }
  );

  const nullOption: VendorOption | null = nullLabel ? { label: nullLabel, value: NULL_VENDOR_VALUE, vendor: null } : null;

  const mappedQuickOptions = useMemo<VendorOption[]>(
    () => (quickOptions ?? []).map((o) => ({ label: o.label, value: o.value, vendor: null })),
    [quickOptions],
  );

  const defaultOptions = useMemo<VendorOption[] | undefined>(() => {
    const base = nullOption ? [nullOption, ...mappedQuickOptions] : mappedQuickOptions;
    return base.length > 0 ? base : undefined;
  }, [nullOption, mappedQuickOptions]);

  const loadOptions = useCallback(async (inputValue: string): Promise<VendorOption[]> => {
    const prefix = nullOption ? [nullOption] : [];
    if (!inputValue || inputValue.length < 3) return prefix;

    const searchId = ++searchIdRef.current;
    await new Promise((r) => setTimeout(r, 300));
    if (searchId !== searchIdRef.current) return prefix;

    try {
      const result = await triggerSearch({ search: inputValue, limit: 10, page: 1 }).unwrap();
      if (searchId !== searchIdRef.current) return prefix;
      return [
        ...prefix,
        ...(result.data || []).map((v) => ({
          label: `${v.name} (${v.id.slice(0, 8)}...)`,
          value: v.id,
          vendor: v,
        })),
      ];
    } catch {
      return prefix;
    }
  }, [triggerSearch, nullOption]);

  const handleChange = useCallback((option: VendorOption | null) => {
    if (!option) {
      labelCacheRef.current = null;
      onSelectedVendorChange(undefined);
      return;
    }
    if (option.value === NULL_VENDOR_VALUE) {
      labelCacheRef.current = null;
      onSelectedVendorChange(null);
      return;
    }
    labelCacheRef.current = { vendorId: option.value, label: option.label };
    onSelectedVendorChange(option.value);
  }, [onSelectedVendorChange]);

  const displayLabel = selectedVendorId
    ? (hasCachedLabel ? labelCacheRef.current!.label : `${selectedVendor?.name ?? selectedVendorId} (${selectedVendorId.slice(0, 8)}...)`)
    : '';

  const currentValue = selectedVendorId === null && nullOption
    ? nullOption
    : selectedVendorId
      ? { label: displayLabel, value: selectedVendorId, vendor: selectedVendor ?? null }
      : null;

  return (
    <AsyncSelect
      loadOptions={loadOptions}
      onChange={handleChange}
      defaultOptions={defaultOptions}
      value={currentValue}
      placeholder={placeholder}
      noOptionsMessage={({ inputValue }) => inputValue.length < 3 ? 'Type at least 3 characters' : 'No vendors found'}
      isClearable={!isDisabled}
      isDisabled={isDisabled}
      styles={{
        control: (base) => ({
          ...base,
          minHeight: '36px',
          fontSize: '14px',
        }),
        // Above MesaTable's sticky headers (as in ClientFilterSelect).
        menu: (base) => ({ ...base, zIndex: 31 }),
      }}
    />
  );
}

export default VendorSelect;
